/**
 * 联想问题组件
 * 显示在AI回答下方的推荐问题
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Spin } from '@arco-design/web-react';
import type { EnhancedSuggestionQuestion } from '@/utils/suggestionManager';
import styles from './index.module.less';

interface SuggestionQuestionsProps {
  questions: EnhancedSuggestionQuestion[];
  onQuestionClick: (question: string) => void;
  visible?: boolean;
  loadingTimeout?: number; // 加载超时时间（毫秒）
}

// 数据验证函数
function validateQuestion(question: any): question is EnhancedSuggestionQuestion {
  return (
    question &&
    typeof question === 'object' &&
    typeof question.text === 'string' &&
    question.text.trim().length > 0
  );
}

export default function SuggestionQuestions({
  questions,
  onQuestionClick,
  visible = true,
  loadingTimeout = 10000, // 默认10秒超时
}: SuggestionQuestionsProps) {
  const [isLoadingTimeout, setIsLoadingTimeout] = useState(false);
  const [clickedQuestions, setClickedQuestions] = useState<Set<string>>(new Set());
  const timeoutRef = useRef<NodeJS.Timeout>();
  const requestInProgress = useRef<boolean>(false);

  // 验证和过滤问题数据
  const validQuestions = React.useMemo(() => {
    if (!Array.isArray(questions)) {
      console.warn('[SuggestionQuestions] questions 不是数组:', questions);
      return [];
    }
    return questions.filter(validateQuestion).slice(0, 2);
  }, [questions]);

  const hasQuestion = validQuestions.length > 0;
  const isLoading = !hasQuestion && !isLoadingTimeout;

  // 加载超时处理
  useEffect(() => {
    if (isLoading && visible) {
      timeoutRef.current = setTimeout(() => {
        setIsLoadingTimeout(true);
      }, loadingTimeout);
    } else {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setIsLoadingTimeout(false);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isLoading, visible, loadingTimeout]);

  // 防重复点击的问题处理函数
  const handleQuestionClick = useCallback(
    (question: EnhancedSuggestionQuestion) => {
      // 防止重复请求
      if (requestInProgress.current) {
        console.warn('[SuggestionQuestions] 请求正在进行中，忽略重复点击');
        return;
      }

      // 防止点击相同问题
      const questionKey = `${question.text}_${question.id || ''}`;
      if (clickedQuestions.has(questionKey)) {
        console.warn('[SuggestionQuestions] 问题已被点击过:', question.text);
        return;
      }

      requestInProgress.current = true;
      setClickedQuestions((prev) => new Set(prev).add(questionKey));

      try {
        onQuestionClick(question.text);
      } catch (error) {
        console.error('[SuggestionQuestions] 处理问题点击时出错:', error);
        // 出错时移除已点击标记，允许重试
        setClickedQuestions((prev) => {
          const newSet = new Set(prev);
          newSet.delete(questionKey);
          return newSet;
        });
      } finally {
        // 延迟重置请求状态，防止快速重复点击
        setTimeout(() => {
          requestInProgress.current = false;
        }, 1000);
      }
    },
    [onQuestionClick, clickedQuestions]
  );

  if (!visible) {
    return null;
  }

  return (
    <div className={`${styles.suggestionContainer} rtc-suggestion-container`}>
      <div className={`${styles.suggestionTitle} rtc-suggestion-title`}>猜你想问</div>
      {hasQuestion ? (
        <div className={styles.questionsWrapper}>
          {validQuestions.map((question, index) => {
            const questionKey = `${question.text}_${question.id || ''}`;
            const isClicked = clickedQuestions.has(questionKey);

            return (
              <button
                key={question.id || `question-${index}`}
                className={`${styles.questionBtn} rtc-question-btn ${styles[`question${index + 1}`]} ${
                  isClicked ? `${styles.clicked} rtc-question-clicked` : ''
                }`}
                onClick={() => handleQuestionClick(question)}
                disabled={isClicked || requestInProgress.current}
              >
                <span className={`${styles.questionIcon} rtc-question-icon`}>?</span>
                <span className={`${styles.questionText} rtc-question-text`}>{question.text}</span>
              </button>
            );
          })}
        </div>
      ) : isLoadingTimeout ? (
        <div className={`${styles.timeoutWrapper} rtc-suggestion-timeout`}>
          <span className={styles.timeoutText}>暂无推荐问题</span>
        </div>
      ) : (
        <div className={`${styles.loadingWrapper} rtc-suggestion-loading`}>
          <Spin size={16} />
        </div>
      )}
    </div>
  );
}
